import { logError, logWarn } from '@ardoq/logging';
import { isTruthy } from '@ardoq/common-helpers';
import { partition, trim } from 'lodash';
import { getCssRulesFromStylesheet } from './getCssRulesFromStylesheet';
import { PartialCSSStyleSheet } from './types';

export const getAllFontFaceRules = (
  sheets: PartialCSSStyleSheet[]
): [dataUrlRules: CSSFontFaceRule[], resourceRules: CSSFontFaceRule[]] =>
  partition(
    sheets
      .flatMap(getCssRulesFromStylesheet)
      .filter(
        (rule): rule is CSSFontFaceRule => rule instanceof CSSFontFaceRule
      ),
    rule => fontDataUrlRegExp.test(rule.style.getPropertyValue('src'))
  );

export const inlineFontFaceRules = async (fontFaceRules: CSSFontFaceRule[]) => {
  const fontFaceRulesByUrl: Record<
    string,
    { ruleData: Record<string, string>; format: string }
  > = Object.fromEntries(
    fontFaceRules
      .map(rule => {
        const { src, format, ruleData } = extractSrcFormatAndRuleData(rule);

        return (src && format ? [src, { format, ruleData }] : []) as [
          src: string | undefined,
          data:
            | { format: string; ruleData: Record<string, string> }
            | undefined,
        ];
      })
      // We use only relative URLs for our own font resources. But we also load
      // plugins in our app, like e.g. intercom, which have their own
      // fonts, so that check is to prevent us from trying to load these
      // resources too. Currently trying to load them seems to cause a lot of
      // log error noise.
      .filter(([src]) => Boolean(src) && src?.startsWith('/'))
  );

  const results: PromiseSettledResult<[url: string, data: string]>[] =
    await Promise.allSettled(
      Object.keys(fontFaceRulesByUrl).map(url =>
        getResourceAsDataURL(url).then<[url: string, data: string]>(data => [
          url,
          data,
        ])
      )
    );

  const [succeeded, failed] = partition(
    results,
    (result): result is PromiseFulfilledResult<[url: string, data: string]> =>
      result.status === 'fulfilled'
  );

  failed.forEach(result => logError(Error(result.reason)));

  return succeeded.map(({ value: [url, dataUrl] }) => {
    const { format, ruleData } = fontFaceRulesByUrl[url];
    return {
      cssText: fontFaceRuleDataToCssText({
        ...ruleData,
        src: `url("${dataUrl}") format("${format}")`,
      }),
    };
  });
};

const fontDataUrlRegExp = /data:font/;
const r = String.raw;
const reString = r`"[^"]*"`;
const notCommaOrString = r`(?:[^,"\s]|${reString}|\s)*?`;
const woffFormatRegExp = new RegExp(
  r`${notCommaOrString}format\("?woff2?"?\)${notCommaOrString}`,
  'gi'
);
const urlRegExp = new RegExp(r`url\((${reString}|[^")]*)\)`);
const SRC_GROUP = 1;
const formatRegExp = new RegExp(r`format\((${reString}|[^")]*)\)`);
const FORMAT_GROUP = 1;

const extractSrcFormatAndRuleData = (rule: CSSFontFaceRule) => {
  const matches = rule.style.getPropertyValue('src').match(woffFormatRegExp);
  const srcAndFormat =
    matches?.find(src => src.includes('woff2')) || matches?.[0];
  const src = trim(srcAndFormat?.match(urlRegExp)?.[SRC_GROUP], '"');
  const format = trim(srcAndFormat?.match(formatRegExp)?.[FORMAT_GROUP], '"');

  return { src, format, ruleData: getFontFaceRuleData(rule) };
};

const getFontFaceRuleData = (rule: CSSFontFaceRule) =>
  Object.fromEntries(
    [
      'font-family',
      'src',
      'unicode-range',
      'font-variant',
      'font-feature-settings',
      'font-variation-settings',
      'font-stretch',
      'font-weight',
      'font-style',
      'size-adjust',
      'ascent-override',
      'descent-override',
      'line-gap-override',
    ]
      .map<[property: string, value: string]>((property: string) => [
        property,
        rule.style.getPropertyValue(property),
      ])
      .filter(([, value]: [string, string]) => isTruthy(value))
  );

const fontFaceRuleDataToCssText = (ruleData: Record<string, string>) =>
  `@font-face {${Object.entries(ruleData)
    .map(([property, value]) => `${property}: ${value};`)
    .join('')}}`;

const getResourceAsDataURL = async (url: string) =>
  new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const errorHandler = () =>
      reject(`Loading font failed, status: ${xhr.status}`);
    xhr.onerror = errorHandler;
    xhr.onabort = errorHandler;
    xhr.ontimeout = errorHandler;
    xhr.onload = () => {
      if (xhr.status !== 200) {
        return errorHandler();
      }
      try {
        const reader = new FileReader();
        reader.onloadend = function () {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
          } else {
            logWarn(Error('No string result for FileReader'));
          }
        };
        reader.readAsDataURL(xhr.response);
      } catch (error) {
        reject('Reading font file failed');
      }
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  });
